import { Button } from "@dash/form";
import { Dialog } from "@dash/dialog";
import { useDeviceType } from "@dash/mq";
import { Spinner } from "@dash/spinner";
import { useTheme } from "@emotion/react";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useQueryClient } from "react-query";
import { MdOutlineQrCodeScanner } from "react-icons/md";
import { useNavigate } from "react-router-dom-v5-compat";

import { IamAuthContext } from "@megaron/auth-react";
import { useToast } from "@dash/toast";
import { useClientManager } from "@megaron/react-clients";
import { InvoiceThreadDoc } from "@megaron/invoices-contracts";
import { Uuid } from "@megaron/uuid";

import { parseKsefQrContent } from "./parseKsefQrContent";
import { isKsefVerificationLink } from "./parseKsefQrContent";
import { useKsefQrScanner } from "./useKsefQrScanner";
import { GroupFilter } from "./useThreadFilters";
import { getMegaraxPath } from "@megaron/megarax-uri";

type Props = {
  groupFilter?: GroupFilter;
};

export const ScanKsefButton: React.FC<Props> = ({ groupFilter = "all" }) => {
  const theme = useTheme();
  const { isMobile } = useDeviceType();
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const auth = useContext(IamAuthContext);
  const userEmail = auth.iamUser?.userType === "megarax" ? auth.iamUser?.email : undefined;
  const docsClient = useClientManager("docs");
  const invoicesClient = useClientManager("invoices");
  const inviteMutation = invoicesClient.inviteParticipants().useMutation();
  const fetchKsefNumberMutation = invoicesClient.getKsefNumberFromVerificationUrl().useMutation();

  const [isOpen, setIsOpen] = useState(false);
  const [ksefPrefix, setKsefPrefix] = useState<string | null>(null);
  const lastScannedUrlRef = useRef<string | null>(null);
  const handledPrefixRef = useRef<string | null>(null);
  const stopScannerRef = useRef<(() => void) | null>(null);
  const handleCloseRef = useRef<() => void>(() => undefined);
  const addAndGoRef = useRef<((thread: InvoiceThreadDoc) => void) | null>(null);
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const searchQuery = docsClient.searchInvoiceThreads().useQuery(
    {
      searchText: ksefPrefix ?? "",
      limit: 20,
      offset: 0,
      isDigital: "any",
      isReceived: "any",
      isPosted: "any",
      isOverdue: "any",
      isMine: groupFilter === "mine" ? "true" : "any",
    },
    { enabled: ksefPrefix !== null && ksefPrefix.length > 0 },
  );

  const items = searchQuery.data?.items ?? [];
  const showPickList = items.length > 1;
  const showScanner = isOpen && !showPickList;
  const isFetchingKsefNumber = fetchKsefNumberMutation.isLoading;

  const handleClose = useCallback(() => {
    setIsOpen(false);
    setKsefPrefix(null);
    handledPrefixRef.current = null;
    lastScannedUrlRef.current = null;
    stopScannerRef.current?.();
  }, []);

  handleCloseRef.current = handleClose;

  const handleScanResult = useCallback(
    (url: string) => {
      if (lastScannedUrlRef.current === url) return;
      lastScannedUrlRef.current = url;

      const applyPrefix = (prefix: string) => {
        if (!prefix || prefix.trim() === "") {
          toast.error("Skanuj KSeF", "Nieprawidłowy kod QR – użyj linku z qr.ksef.mf.gov.pl");
          return;
        }
        stopScannerRef.current?.();
        handledPrefixRef.current = null;
        setKsefPrefix(prefix);
      };

      if (isKsefVerificationLink(url)) {
        fetchKsefNumberMutation.mutate(
          { url },
          {
            onSuccess: (data) => {
              applyPrefix(data.ksefNumber ?? parseKsefQrContent(url));
            },
            onError: () => {
              applyPrefix(parseKsefQrContent(url));
            },
          },
        );
      } else {
        applyPrefix(parseKsefQrContent(url));
      }
    },
    [toast, fetchKsefNumberMutation],
  );

  const {
    start: startScanner,
    stop: stopScanner,
    cameraError,
  } = useKsefQrScanner({
    onResult: handleScanResult,
  });
  stopScannerRef.current = stopScanner;

  const addAndGo = useCallback(
    (thread: InvoiceThreadDoc) => {
      handleClose();
      navigate(getMegaraxPath.uri("megarax://invoices/threads/id/{id}", { id: thread.threadUuid }));
      if (!userEmail) return;
      const threadUuid = Uuid(thread.threadUuid);
      inviteMutation.mutate(
        { threadUuid, users: [userEmail] },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["docs", "searchInvoiceThreads"] });
          },
          onError: () => {
            toast.error("Błąd", "Nie udało się dołączyć do wątku");
          },
        },
      );
    },
    [handleClose, navigate, userEmail, inviteMutation, queryClient, toast],
  );
  addAndGoRef.current = addAndGo;

  useEffect(() => {
    if (!isOpen || showPickList) stopScanner();
  }, [isOpen, showPickList, stopScanner]);

  useEffect(() => {
    if (searchQuery.isError && ksefPrefix) {
      if (handledPrefixRef.current === ksefPrefix) return;
      handledPrefixRef.current = ksefPrefix;
      toastRef.current.error("Skanuj KSeF", "Nie udało się wyszukać wątków.");
      handleCloseRef.current();
      return;
    }
    if (!searchQuery.data || !ksefPrefix) return;
    if (handledPrefixRef.current === ksefPrefix) return;
    const { count } = searchQuery.data;
    if (count === 0) {
      handledPrefixRef.current = ksefPrefix;
      toastRef.current.error("Skanuj KSeF", "Nie znaleziono wątków dla tego numeru KSeF.");
      handleCloseRef.current();
      return;
    }
    if (count === 1) {
      handledPrefixRef.current = ksefPrefix;
      const thread = searchQuery.data.items[0];
      handleCloseRef.current();
      const go = addAndGoRef.current;
      if (go) go(thread);
      return;
    }
    handledPrefixRef.current = ksefPrefix;
  }, [searchQuery.data, searchQuery.isError, ksefPrefix]);

  const goToThread = useCallback(
    (thread: InvoiceThreadDoc) => {
      addAndGo(thread);
    },
    [addAndGo],
  );

  return (
    <>
      <Button icon={<MdOutlineQrCodeScanner size={18} />} onClick={() => setIsOpen(true)} variant="outline">
        Skanuj KSeF
      </Button>
      {isOpen && (
        <Dialog
          onClose={handleClose}
          placement={isMobile ? "fullscreen" : "center"}
          header={showPickList ? "Wybierz wątek" : "Skanuj KSeF"}
          css={{
            width: isMobile ? "100%" : "min(90vw, 420px)",
            maxWidth: "100%",
            height: isMobile ? "100%" : "auto",
            maxHeight: isMobile ? "100%" : "90vh",
            display: "flex",
            flexDirection: "column",
            minHeight: isMobile ? "100%" : 0,
            overflow: "hidden",
          }}
        >
          {showPickList ? (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "8px",
                flex: 1,
                minHeight: 0,
                overflow: "auto",
              }}
            >
              {ksefPrefix && (
                <p css={{ fontSize: "0.875rem", color: theme.colors.secondaryText, margin: 0, flexShrink: 0 }}>
                  Prefiks KSeF: <strong css={{ color: theme.colors.primary }}>{ksefPrefix}</strong>
                </p>
              )}
              <div
                css={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  flex: 1,
                  minHeight: 0,
                  overflow: "auto",
                }}
              >
                {items.map((thread) => (
                  <Button
                    key={thread.threadUuid}
                    variant="outline"
                    onClick={() => goToThread(thread)}
                    css={{
                      justifyContent: "flex-start",
                      textAlign: "left",
                      padding: "12px",
                      background: theme.colors.primaryLight,
                    }}
                  >
                    {thread.referenceNumber} – {thread.title}
                  </Button>
                ))}
              </div>
            </div>
          ) : (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                flex: 1,
                minHeight: 0,
                overflow: "hidden",
              }}
            >
              {ksefPrefix && (
                <p css={{ fontSize: "0.875rem", color: theme.colors.secondaryText, margin: 0, flexShrink: 0 }}>
                  Prefiks KSeF: <strong css={{ color: theme.colors.primary }}>{ksefPrefix}</strong>
                </p>
              )}
              {showScanner && (
                <div
                  css={{
                    position: "relative",
                    width: "100%",
                    flex: 1,
                    minHeight: isMobile ? "200px" : "280px",
                    maxHeight: isMobile ? "none" : "min(60vh, 420px)",
                    aspectRatio: isMobile ? undefined : "1",
                    borderRadius: theme.smallBorderRadius,
                    overflow: "hidden",
                    background: "black",
                  }}
                >
                  <video
                    ref={(el) => {
                      if (el) startScanner(el);
                    }}
                    css={{ width: "100%", height: "100%", objectFit: "cover" }}
                    muted
                    playsInline
                  />
                  {cameraError && (
                    <div
                      css={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0,0,0,0.7)",
                        color: "white",
                        fontSize: "14px",
                        padding: "1rem",
                        textAlign: "center",
                      }}
                    >
                      Brak dostępu do kamery.
                    </div>
                  )}
                  {((searchQuery.isFetching && ksefPrefix) || isFetchingKsefNumber) && (
                    <div
                      css={{
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: "rgba(0,0,0,0.5)",
                      }}
                    >
                      <Spinner size="32px" color="white" />
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </Dialog>
      )}
    </>
  );
};
